/**
 * Header patterns for every mermaid form the markdown pipeline renders.
 *
 * Each pattern matches an *exact* mermaid header line. Matching one hands the
 * corresponding component ownership of the block including its failure — an
 * unparseable body resolves to `CmarkdownvisualError`, never to a vanished
 * block. That is why the patterns anchor both ends of the header line instead
 * of testing a `\b` prefix: `/^\s*timeline\b/` also matches a fence opening
 * `timeline notes for Q3`, and `/^\s*(?:flowchart|graph)\b/` matches
 * `graph theory notes`. Claiming those would replace readable prose with an
 * error message.
 *
 * Only `pie`, `flowchart`/`graph` and `xychart` legitimately carry content on
 * the header line itself, so only those three accept a tail.
 *
 * Content matching no pattern — including valid but unsupported mermaid forms
 * (`classDiagram`, `erDiagram`, `stateDiagram`, `gitGraph`, `journey`) — keeps
 * its raw source as a plain code block.
 *
 * This module is deliberately free of React so the guide-corpus round-trip
 * assert can route fences through the very same patterns the renderer uses.
 */

export enum MermaidVisualForm {
  Pie = 'pie',
  Flowchart = 'flowchart',
  XyChart = 'xychart',
  Mindmap = 'mindmap',
  Gantt = 'gantt',
  Timeline = 'timeline',
  Kanban = 'kanban',
  Sequence = 'sequence',
  Treemap = 'treemap',
  Quadrant = 'quadrant',
}

export const MERMAID_HEADER_PATTERN_BY_FORM: Record<MermaidVisualForm, RegExp> = {
  // `pie`, `pie showData`, `pie title X`, `pie showData title X`.
  [MermaidVisualForm.Pie]: /^[ \t]*pie(?:[ \t]+showData)?(?:[ \t]+title[ \t]+\S.*?)?[ \t]*$/im,
  // The direction token is mandatory, which is what excludes `graph theory`.
  [MermaidVisualForm.Flowchart]: /^[ \t]*(?:flowchart|graph)[ \t]+(?:TB|TD|BT|RL|LR)\b/im,
  [MermaidVisualForm.XyChart]: /^[ \t]*xychart(?:-beta)?(?:[ \t]+horizontal)?[ \t]*$/im,
  [MermaidVisualForm.Mindmap]: /^[ \t]*mindmap[ \t]*$/im,
  [MermaidVisualForm.Gantt]: /^[ \t]*gantt[ \t]*$/im,
  [MermaidVisualForm.Timeline]: /^[ \t]*timeline[ \t]*$/im,
  [MermaidVisualForm.Kanban]: /^[ \t]*kanban[ \t]*$/im,
  [MermaidVisualForm.Sequence]: /^[ \t]*sequenceDiagram[ \t]*$/im,
  [MermaidVisualForm.Treemap]: /^[ \t]*treemap(?:-beta)?[ \t]*$/im,
  [MermaidVisualForm.Quadrant]: /^[ \t]*quadrantChart[ \t]*$/im,
};

/**
 * Dispatch order. The renderer resolves the first matching entry, so this array
 * order is the precedence rule whenever two patterns can match one fence.
 */
export const MERMAID_VISUAL_FORM_ORDER: MermaidVisualForm[] = [
  MermaidVisualForm.Pie,
  MermaidVisualForm.Flowchart,
  MermaidVisualForm.XyChart,
  MermaidVisualForm.Mindmap,
  MermaidVisualForm.Gantt,
  MermaidVisualForm.Timeline,
  MermaidVisualForm.Kanban,
  MermaidVisualForm.Sequence,
  MermaidVisualForm.Treemap,
  MermaidVisualForm.Quadrant,
];

/**
 * The CABAL `map` block, accepted inside a mermaid fence because models reach
 * for ```mermaid by reflex. It is deliberately NOT a `MermaidVisualForm`: it is
 * not a mermaid diagram type, and the guide-corpus round trip governs mermaid
 * forms only.
 *
 * Unlike the diagram headers this anchors to the START of the block rather than
 * to any line (`/m`): `map` is a short, ordinary English word that can appear
 * as a node label or a task name deep inside an unrelated fence, and claiming
 * such a fence would replace a readable block with a map. Only leading blank
 * lines may precede it.
 */
export const MAP_HEADER_PATTERN = /^(?:[ \t]*\r?\n)*[ \t]*map[ \t]*(?:\r?\n|$)/;

export enum MermaidFenceHeaderKind {
  /** A mermaid diagram type. */
  Visual = 'visual',
  /** The CABAL map block. */
  Map = 'map',
}

export interface MermaidFenceHeaderEntry {
  kind: MermaidFenceHeaderKind;
  /** The mermaid form, or null for kinds that are not mermaid diagrams. */
  form: MermaidVisualForm | null;
  pattern: RegExp;
}

/**
 * Every header pattern a ```mermaid fence is tested against, in dispatch order.
 * The renderer resolves the FIRST match, so this array is the precedence rule
 * and the dispatch table is built from it — there is no second ordering to
 * drift out of sync.
 *
 * The map entry is registered LAST on purpose: see MAP_HEADER_PATTERN.
 */
export const MERMAID_FENCE_HEADER_ORDER: MermaidFenceHeaderEntry[] = [
  ...MERMAID_VISUAL_FORM_ORDER.map((form) => ({
    kind: MermaidFenceHeaderKind.Visual,
    form,
    pattern: MERMAID_HEADER_PATTERN_BY_FORM[form],
  })),
  {
    kind: MermaidFenceHeaderKind.Map,
    form: null,
    pattern: MAP_HEADER_PATTERN,
  },
];

export function selectMermaidVisualForm(content: string): MermaidVisualForm | null {
  return (
    MERMAID_VISUAL_FORM_ORDER.find((form) =>
      MERMAID_HEADER_PATTERN_BY_FORM[form].test(content),
    ) ?? null
  );
}
